<template>
  <div class="header">
    <div class="container-fluid">
      <div class="card" style="margin-top: 50px">
        <Header />
        <div class="card-body">
          <div v-if="!showEmployeeList" class="approval-hero mb-4">
            <div class="approval-filter-panel">
              <div class="approval-filter-header">
                <div>
                  <h5 class="mb-1">Approval Scope</h5>
                  <small class="text-muted">Prioritas karyawan dengan approval pending, pencarian mencakup semua karyawan BIB.</small>
                </div>
                <span class="approval-count-pill">{{ pendingApprovalCount }} pengajuan</span>
              </div>

              <div class="approval-filter-grid">
                <div class="approval-control">
                  <label>IDP Period</label>
                  <v-select
                    :options="idpPeriodOptions"
                    v-model="selectedPeriodOption"
                    label="text"
                    placeholder="-- Select a period --"
                  />
                </div>
              </div>
            </div>

            <div class="approval-info-grid">
              <div class="approval-info-card">
                <span>Employee</span>
                <strong>{{ selectedEmployeeName }}</strong>
                <small>{{ filters.nik || '-' }}</small>
              </div>
              <div class="approval-info-card">
                <span>Position</span>
                <strong>{{ position || '-' }}</strong>
                <small>Current selected employee</small>
              </div>
              <button type="button" class="btn btn-sm btn-outline-secondary" @click="backToEmployeeList">
                <i class="fe fe-arrow-left mr-1"></i>
                Back to list
              </button>
            </div>
          </div>

          <div v-if="showEmployeeList" class="approval-list-shell mb-4">
            <div class="tna-summary-grid mb-4">
              <div class="tna-summary-card">
                <div>
                  <span class="summary-eyebrow">Karyawan</span>
                  <strong>{{ employeeListTotalRecords }}</strong>
                  <small>Dalam scope monitoring</small>
                </div>
                <i class="fe fe-users"></i>
              </div>
              <div class="tna-summary-card primary">
                <div>
                  <span class="summary-eyebrow">Pengajuan Page Ini</span>
                  <strong>{{ employeeListTotalSubmissions }}</strong>
                  <small>Menunggu approval pada daftar tampil</small>
                </div>
                <i class="fe fe-layers"></i>
              </div>
              <div class="tna-summary-card">
                <div>
                  <span class="summary-eyebrow">Page Ini</span>
                  <strong>{{ subordinates.length }}</strong>
                  <small>Karyawan pada halaman ini</small>
                </div>
                <i class="fe fe-list"></i>
              </div>
            </div>

            <div class="approval-filter-panel">
              <div class="approval-filter-header">
                <div>
                  <h5 class="mb-1">Approval Scope</h5>
                  <small class="text-muted">Daftar menampilkan karyawan BIB sesuai scope. Pengajuan enrollment yang menunggu approval tampil paling atas.</small>
                </div>
              </div>
              <div class="approval-filter-grid">
                <div class="approval-control">
                  <label>IDP Period</label>
                  <v-select :options="idpPeriodOptions" v-model="selectedPeriodOption" label="text" placeholder="-- Select a period --" />
                </div>
              </div>
            </div>
          </div>

          <Tabs
            :activeTab="activeTab"
            @tab-changed="onTabChange"
            :is-loading="isLoading"
          />

          <ApprovalEmployeeList
            v-if="showEmployeeList"
            :rows="subordinates"
            :search.sync="employeeListSearch"
            :is-loading="isLoadingEmployeeList"
            :total="employeeListTotalRecords"
            :page="employeeListPage"
            :page-size="employeeListPageSize"
            :showing-from="employeeListShowingFrom"
            :showing-to="employeeListShowingTo"
            @search="onEmployeeListSearch"
            @detail="selectApprovalEmployee"
            @page-change="onEmployeeListPageChange"
            @per-page-change="onEmployeeListPerPageChange"
          />

          <template v-else>
          <Summary
            :isLoadingSummary="isLoadingSummary"
            :summaryData="summaryIdpLearningReport"
          />

          <div class="course-list-toolbar mb-4">
            <div class="course-list-title">
              <h4 class="mb-1 font-weight-bold">Course List</h4>
              <small class="text-muted">Pantau semua pengajuan pada periode dan learning type yang dipilih.</small>
            </div>
            <div class="course-list-actions">
              <b-form-input
                :disabled="!filters.nik"
                class="course-search-input"
                v-model="filters.search"
                placeholder="Search training..."
                @keyup.enter="onSearch"
              />
            </div>
          </div>

          <EnrollmentReportTable
            :rows="idpLearningReport.data"
            :total="idpLearningReport.totalItems"
            :showing-from="showingFrom"
            :showing-to="showingTo"
            :page="filters.page"
            :page-size="filters.pageSize"
            :selected-rows="selectedRows"
            :is-loading-approve="isLoadingApprove"
            :learning-type="filters.learning_type"
            @sort-change="onSortChange"
            @page-change="onPageChange"
            @per-page-change="onPerPageChange"
            @page-change-from-bv="onPageChangeFromBv"
            @select-change="onSelectChange"
            @approve="handleApprove"
            @reject="handleReject"
            @view-detail="handleViewDetail"
            @feedback-project="showFeedbackConfirmModal"
            @approve-project="showApproveConfirmModal"
            @reject-project="showRejectConfirmModal"
          />

          <StructuredLearningModal
            v-if="filters.learning_type === 'structured_learning'"
            :modal-id="'structured-learning-modal'"
            :data="structuredLearningDetail"
            :is-loading="isModalLoading"
            @close="showModal = false"
          />

          <SocialLearningModal
            v-if="filters.learning_type === 'social_learning'"
            :modal-id="'social-learning-modal'"
            :data="socialLearningDetail"
            :is-loading="isModalLoading"
            @close="showModal = false"
          />

          <ExperientialLearningModal
            v-if="filters.learning_type === 'experiential_learning'"
            :modal-id="'experiential-learning-modal'"
            :data="experientialLearningDetail"
            :is-loading="isModalLoading"
            @close="showModal = false"
          />

          <ConfirmationSubmitModal
            :showConfirmationSubmit="showConfirmationSubmitModal"
            @close="showConfirmationSubmitModal = false"
            :selectedItems="selectedRows"
            :isSubmitting="isSubmitting"
            @submit="approveEnrollment"
            :learning-type="filters.learning_type"
          />

          <ConfirmationSubmitModal
            :showConfirmationSubmit="showRejectConfirmationModal"
            @close="showRejectConfirmationModal = false"
            :selectedItems="selectedRows"
            :isSubmitting="isSubmitting"
            @submit="rejectEnrollment"
            :learning-type="filters.learning_type"
            :is-reject="true"
          />

          <ActionProjectModal
            :modal-id="'action-project-modal'"
            :is-loading="isSubmitting"
            :data="actionProject.data"
            :type="actionProject.type"
            @submit-action="submitActionProject"
          />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import logic from "./main.logic";
import ApprovalEmployeeList from "../components/approval-employee-list.vue";
import Header from "./components/header.vue";
import Summary from "../enrollment/components/summary.vue";
import StructuredLearningModal from "./modal/structured-learning-modal.vue";
import EnrollmentReportTable from "./table/enrollment-report-table.vue";
import ConfirmationSubmitModal from "./modal/confirmation-submit-modal.vue";
import Tabs from "./components/tabs.vue";
import SocialLearningModal from "./modal/social-learning-modal.vue";
import ExperientialLearningModal from "./modal/experiential-learning-modal.vue";
import ActionProjectModal from "./modal/action-project-modal.vue";

export default {
  name: "IDPEnrollmentReportPage",
  components: {
    ApprovalEmployeeList,
    Header,
    Summary,
    StructuredLearningModal,
    ConfirmationSubmitModal,
    EnrollmentReportTable,
    Tabs,
    SocialLearningModal,
    ExperientialLearningModal,
    ActionProjectModal,
  },
  mixins: [logic],
};
</script>

<style scoped>
.approval-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(280px, 0.9fr);
  gap: 16px;
}

.tna-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.tna-summary-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 92px;
  border: 1px solid #e9edf5;
  border-radius: 8px;
  background: #fff;
  padding: 18px;
  box-shadow: 0 8px 22px rgba(31, 45, 61, 0.04);
}

.tna-summary-card.primary {
  border-color: #ffd3d6;
  background: #fffafb;
}

.tna-summary-card strong {
  display: block;
  color: #1f2937;
  font-size: 28px;
  line-height: 1;
  margin: 6px 0;
}

.tna-summary-card small,
.summary-eyebrow {
  color: #8aa0c7;
  font-weight: 700;
}

.tna-summary-card i {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 8px;
  background: #fff1f2;
  color: #e11d2e;
  font-size: 20px;
}

.approval-filter-panel,
.approval-info-card {
  border: 1px solid #e9edf5;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 22px rgba(31, 45, 61, 0.04);
}

.approval-filter-panel {
  padding: 16px;
}

.approval-filter-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.approval-count-pill {
  white-space: nowrap;
  border-radius: 999px;
  background: #fff1f2;
  color: #b4232e;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 12px;
}

.approval-filter-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(160px, 1fr));
  gap: 14px;
}

.approval-control label,
.approval-info-card span {
  display: block;
  color: #4b587c;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 6px;
}

.approval-info-grid {
  display: grid;
  gap: 12px;
}

.approval-info-card {
  min-height: 88px;
  padding: 16px;
}

.approval-info-card strong {
  display: block;
  color: #1f2937;
  font-size: 16px;
  line-height: 1.25;
  word-break: break-word;
}

.approval-info-card small {
  color: #8ea1bf;
  font-weight: 600;
}

.employee-option {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.employee-option strong {
  color: #1f2937;
  font-size: 13px;
}

.employee-option small {
  color: #8ea1bf;
  font-weight: 600;
}

.course-list-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border: 1px solid #eef1f6;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 22px rgba(31, 45, 61, 0.04);
}

.course-list-title {
  min-width: 220px;
}

.course-list-actions {
  display: grid;
  grid-template-columns: minmax(170px, 190px) minmax(240px, 360px) auto;
  align-items: center;
  gap: 10px;
}

.course-status-filter,
.course-search-input,
.course-filter-btn {
  min-height: 36px;
}

.course-filter-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

@media (max-width: 1200px) {
  .approval-filter-grid {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }
}

@media (max-width: 992px) {
  .approval-hero,
  .approval-filter-grid,
  .course-list-actions {
    grid-template-columns: 1fr;
  }

  .course-list-toolbar {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
